import { useLeagues } from "@/modules/leagues/hooks/useLeagues";
import { useUpcomingMatchesAcrossLeagues } from "@/modules/leagues/hooks/useMatches";
import { MatchCard } from "@/modules/leagues/components/MatchCard";
import { SkeletonCard } from "@/components/atoms/Skeleton";
import { EmptyState } from "@/components/organisms/EmptyState";
import { ErrorState } from "@/components/organisms/ErrorState";
import { StadiumIllustration, TrophyIllustration } from "@/components/atoms/illustrations";

/**
 * Aba "Jogos" — próximos jogos de TODAS as ligas do usuário, agregados numa
 * lista só (Priority 5/6 do escopo "bolão funcional de verdade"). Se o
 * usuário só participa do Brasileirão, só aparecem jogos do Brasileirão;
 * se participar de mais competições, todas aparecem juntas, ordenadas por
 * data/horário.
 */
export function AllMatchesPage() {
  const { data: leagues, isLoading: loadingLeagues, isError: leaguesError, refetch: refetchLeagues } = useLeagues();
  const {
    data: matches,
    isLoading: loadingMatches,
    isError: matchesError,
    refetch: refetchMatches,
  } = useUpcomingMatchesAcrossLeagues(leagues);

  if (loadingLeagues || (leagues && leagues.length > 0 && loadingMatches)) {
    return (
      <div className="space-y-3">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }

  if (leaguesError) {
    return (
      <ErrorState
        title="Não foi possível carregar suas ligas"
        description="Verifique sua conexão e tente novamente."
        onRetry={() => refetchLeagues()}
      />
    );
  }

  if (!leagues || leagues.length === 0) {
    return (
      <EmptyState
        illustration={TrophyIllustration}
        title="Entre numa liga pra ver os jogos"
        description="Os jogos aparecem aqui assim que você participar de pelo menos uma liga."
      />
    );
  }

  if (matchesError) {
    return (
      <ErrorState
        title="Não foi possível carregar os jogos"
        description="Verifique sua conexão e tente novamente."
        onRetry={() => refetchMatches()}
      />
    );
  }

  if (!matches || matches.length === 0) {
    return (
      <EmptyState
        illustration={StadiumIllustration}
        title="Nenhum jogo por aqui ainda"
        description="Assim que a rodada atual das suas ligas tiver jogos, eles aparecem aqui."
      />
    );
  }

  return (
    <div className="space-y-3">
      {matches.map((match) => (
        <div key={`${match.leagueId}-${match.id}`}>
          <p className="mb-1.5 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {match.competitionLabel} · {match.roundLabel}
          </p>
          <MatchCard match={match} leagueId={match.leagueId} scoringRules={match.scoringRules} totalParticipants={match.totalParticipants} />
        </div>
      ))}
    </div>
  );
}
